import { Smartphone, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useTranslation } from '@splitfiesta/i18n';

import { useNativeAppNudge } from '@/hooks/useNativeAppNudge';

/** Routes that render their own inline nudge; the floating banner yields. */
const INLINE_NUDGE_ROUTES = new Set(['/login', '/register', '/groups']);

function hasInlineNudge(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return INLINE_NUDGE_ROUTES.has(path) || path.startsWith('/invite/');
}

/**
 * Floating "get the app" banner for phone browsers. Android links to Play;
 * non-Safari iOS browsers link to the App Store (Safari shows the Smart App
 * Banner itself). Hidden where a page carries an inline nudge so a phone
 * never sees two at once.
 */
export function NativeAppPrompt() {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const { store, url, dismiss } = useNativeAppNudge();

  if (!store || !url || hasInlineNudge(pathname)) return null;

  return (
    <div
      role="dialog"
      aria-label={t('nativeApp.banner.title')}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white p-4 shadow-lg dark:border-gray-800 dark:bg-gray-900"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto flex max-w-md items-center gap-3">
        <div className="rounded-xl bg-primary-50 p-2 dark:bg-primary-500/10">
          <Smartphone className="h-6 w-6 text-primary-500" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">
            {t('nativeApp.banner.title')}
          </p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {t('nativeApp.banner.description')}
          </p>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={dismiss}
          className="shrink-0 rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          {t(
            store === 'ios' ? 'nativeApp.banner.ctaIos' : 'nativeApp.banner.cta'
          )}
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('nativeApp.banner.dismiss')}
          className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
